import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { combineLatest, filter } from 'rxjs';
import { constants } from 'src/app/core/constants/constants';
import { isPresent } from 'src/app/core/utils/isPresent';
import {
  ClauseOptionType,
  DocumentProcessingClause,
  DocumentProcessingPagedReviewResponse,
  FlagType,
  ShortReviewClauseResponseItem,
} from 'src/app/modules/documents/services/document-processing-api.service';
import { AppButtonSize, AppButtonType } from 'src/app/shared/shared.model';
import { WorkspaceDetailsDto, WorkspaceDocumentDto } from '../../../../models/workspaces.model';
import {
  ClauseVarianceDetailedExportFilters,
  ClauseVarianceExportStateService,
} from '../../services/clause-variance-export-state.service';
import {
  selectAnalyzingClauseVariance,
  selectAnalyzingClauseVarianceResponse,
  selectLoadingReviewDataComplete,
  selectNonReferenceDocuments,
  selectPagedReviewDataByDocumentIdMap,
  selectReferenceDocument,
  selectReviewDoneWithErrors,
  selectWorkspaceDetails,
} from '../../../../store/selectors';
import { TranslocoService } from '@ngneat/transloco';

@UntilDestroy()
@Component({
  selector: 'app-analyzing-clause-alignment-detailed',
  templateUrl: './analyzing-clause-alignment-detailed.component.html',
  styleUrl: './analyzing-clause-alignment-detailed.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AnalyzingClauseAlignmentDetailedComponent implements OnInit {
  public btnTypes = AppButtonType;
  public btnSizes = AppButtonSize;
  public isAnalyzingClauseVarianceLoading$ = this.store$.select(selectAnalyzingClauseVariance);
  public workspaceDetails: WorkspaceDetailsDto | null = null;
  public selectedReferenceDocument: WorkspaceDocumentDto | null = null;
  public reviewDocuments: WorkspaceDocumentDto[] = [];
  public form = new FormGroup({
    clauseName: new FormControl<string | null>(null),
    targetDocumentId: new FormControl<string | null>(null),
    legalEquivalence: new FormControl<string | null>(null),
  });
  public allDetailedRows: DetailedClauseAlignmentRowViewModel[] = [];
  public filteredDetailedRows: DetailedClauseAlignmentRowViewModel[] = [];
  public targetDocumentOptions: FilterOptionViewModel[] = [];
  public legalEquivalenceOptions: FilterOptionViewModel[] = [];
  public loadingReviewDataComplete = false;
  public reviewDoneWithErrors = false;
  public constants = constants;
  public DetailedClauseAlignmentStatus = DetailedClauseAlignmentStatus;

  constructor(
    private readonly store$: Store,
    private readonly cdr: ChangeDetectorRef,
    private translocoService: TranslocoService,
    private readonly clauseVarianceExportStateService: ClauseVarianceExportStateService,
  ) {}

  ngOnInit(): void {
    this.iniCollections();
    this.initSubscriptions();
    this.initFormSubscriptions();
    this.startInitialization();
  }

  iniCollections(): void {
    this.legalEquivalenceOptions = [
      {
        id: DetailedClauseAlignmentStatus.Yes,
        name: this.translocoService.translate('buttons.yes'),
      },
      { id: DetailedClauseAlignmentStatus.No, name: this.translocoService.translate('buttons.no') },
    ];
    this.cdr.markForCheck();
  }

  initSubscriptions(): void {
    this.translocoService.langChanges$.pipe(untilDestroyed(this)).subscribe(() => {
      this.iniCollections();
    });
  }

  startInitialization(): void {
    combineLatest([
      this.store$.select(selectWorkspaceDetails).pipe(untilDestroyed(this), filter(isPresent)),
      this.store$.select(selectAnalyzingClauseVarianceResponse).pipe(untilDestroyed(this)),
      this.store$.select(selectReferenceDocument).pipe(untilDestroyed(this)),
      this.store$.select(selectNonReferenceDocuments).pipe(untilDestroyed(this)),
      this.store$.select(selectPagedReviewDataByDocumentIdMap).pipe(untilDestroyed(this)),
      this.store$.select(selectLoadingReviewDataComplete).pipe(untilDestroyed(this)),
      this.store$.select(selectReviewDoneWithErrors).pipe(untilDestroyed(this)),
    ])
      .pipe(untilDestroyed(this))
      .subscribe(
        ([
          workspaceDetails,
          analyzingClauseVarianceResponse,
          selectedReferenceDocument,
          nonReferenceDocuments,
          pagedReviewDataByDocumentId,
          loadingReviewDataComplete,
          reviewDoneWithErrors,
        ]) => {
          this.workspaceDetails = workspaceDetails;
          this.selectedReferenceDocument = selectedReferenceDocument;
          this.reviewDocuments = nonReferenceDocuments ?? [];
          this.targetDocumentOptions = this.reviewDocuments.map((document) => ({
            id: document.id,
            name: document.name,
          }));
          this.allDetailedRows = this.getDetailedClauseAlignmentRows(
            analyzingClauseVarianceResponse?.playbook?.clauses ?? [],
            this.reviewDocuments,
            pagedReviewDataByDocumentId,
          );
          this.applyFilters();
          this.publishDetailedRowsForExport();
          this.loadingReviewDataComplete = loadingReviewDataComplete;
          this.reviewDoneWithErrors = reviewDoneWithErrors;
          this.cdr.markForCheck();
        },
      );
  }

  public clearFilters(): void {
    this.form.reset();
  }

  private initFormSubscriptions(): void {
    this.form.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
      this.applyFilters();
      this.publishDetailedRowsForExport();
      this.cdr.markForCheck();
    });
  }

  private getDetailedClauseAlignmentRows(
    playbookClauses: ShortReviewClauseResponseItem[],
    reviewDocuments: WorkspaceDocumentDto[],
    pagedReviewDataByDocumentId: Record<string, DocumentProcessingPagedReviewResponse>,
  ): DetailedClauseAlignmentRowViewModel[] {
    return reviewDocuments.flatMap((document) => {
      const pagedReviewData = pagedReviewDataByDocumentId[document.id];

      if (!pagedReviewData?.clauses?.length) {
        return [];
      }

      return playbookClauses.flatMap((clause) => {
        const reviewedClause = pagedReviewData.clauses.find(
          (reviewClause) => reviewClause.id === clause.id,
        );

        if (!reviewedClause) {
          return [];
        }

        return [
          {
            id: `${document.id}-${clause.id}`,
            clauseId: clause.id,
            clauseName: clause.name,
            targetDocumentId: document.id,
            targetDocumentName: document.name,
            targetClause: this.getMostDesiredTargetClause(reviewedClause),
            page: this.getProofPageNumbersLabel(reviewedClause.proofPageNumbers),
            legalEquivalence:
              reviewedClause.flagType === FlagType.MostDesired
                ? DetailedClauseAlignmentStatus.Yes
                : DetailedClauseAlignmentStatus.No,
            legalDifferences: this.getTextOrFallback(reviewedClause.legalDifferences),
            legalConsequences: this.getTextOrFallback(reviewedClause.legalConsequences),
          },
        ];
      });
    });
  }

  private getMostDesiredTargetClause(reviewedClause: DocumentProcessingClause): string {
    return (
      reviewedClause.playbookOptions.find((option) => option.type === ClauseOptionType.MostDesired)
        ?.text ?? '-'
    );
  }

  private getProofPageNumbersLabel(proofPageNumbers: number[] | undefined): string {
    return proofPageNumbers?.length ? proofPageNumbers.join(', ') : '-';
  }

  private getTextOrFallback(value?: string): string {
    const trimmedValue = value?.trim();

    return trimmedValue ? trimmedValue : '-';
  }

  private applyFilters(): void {
    const clauseSearchTerm = this.form.controls.clauseName.value ?? '';
    const selectedTargetDocumentId = this.form.controls.targetDocumentId.value ?? '';
    const selectedLegalEquivalence = this.form.controls.legalEquivalence.value ?? '';
    const normalizedClauseSearchTerm = clauseSearchTerm.trim().toLowerCase();

    this.filteredDetailedRows = this.allDetailedRows.filter((row) => {
      const clauseMatches =
        !normalizedClauseSearchTerm ||
        row.clauseName.toLowerCase().includes(normalizedClauseSearchTerm);
      const targetDocumentMatches =
        !selectedTargetDocumentId || row.targetDocumentId === selectedTargetDocumentId;
      const legalEquivalenceMatches =
        !selectedLegalEquivalence || row.legalEquivalence === selectedLegalEquivalence;

      return clauseMatches && targetDocumentMatches && legalEquivalenceMatches;
    });
  }

  private publishDetailedRowsForExport(): void {
    const filters: ClauseVarianceDetailedExportFilters = {
      clauseName: this.form.controls.clauseName.value,
      targetDocumentId: this.form.controls.targetDocumentId.value,
      legalEquivalence: this.form.controls.legalEquivalence.value,
    };

    this.clauseVarianceExportStateService.setDetailedRows(
      this.allDetailedRows,
      this.filteredDetailedRows,
      filters,
    );
  }
}

export enum DetailedClauseAlignmentStatus {
  Yes = 'yes',
  No = 'no',
}

interface DetailedClauseAlignmentRowViewModel {
  id: string;
  clauseId: number;
  clauseName: string;
  targetDocumentId: string;
  targetDocumentName: string;
  targetClause: string;
  page: string;
  legalEquivalence: DetailedClauseAlignmentStatus;
  legalDifferences: string;
  legalConsequences: string;
}

interface FilterOptionViewModel {
  id: string;
  name: string;
}
